import { Drawer, Input, Select, Tag } from 'antd';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import type {
  DesktopFeaturePolicy,
  DesktopFeatureVersionRange,
  DesktopUpdateProduct,
} from '@/types';
import {
  createEmptyDesktopFeature,
  DESKTOP_UPDATE_CHANNELS,
  DESKTOP_UPDATE_TARGETS,
  formatDesktopFeatureRange,
  isDesktopUpdateReleaseAvailable,
} from './desktopUpdatePolicyModel';

interface DesktopFeatureAllowlistEditorProps {
  current?: DesktopUpdateProduct;
  draft: DesktopUpdateProduct;
  product: string;
  readOnly: boolean;
  update(apply: (draft: DesktopUpdateProduct) => void): void;
}

const targetModeOptions = [
  { label: '继承', value: 'inherit' },
  { label: '禁用', value: 'disabled' },
  { label: '自定义范围', value: 'range' },
];

function createRange() {
  return {
    minimumVersionInclusive: '',
    maximumVersionExclusive: '',
  };
}

function updateOptionalRange(
  current: DesktopFeatureVersionRange | undefined,
  field: keyof DesktopFeatureVersionRange,
  value: string,
) {
  const next = {
    minimumVersionInclusive: current?.minimumVersionInclusive || '',
    maximumVersionExclusive: current?.maximumVersionExclusive || '',
    [field]: value,
  };
  return next.minimumVersionInclusive || next.maximumVersionExclusive
    ? next
    : undefined;
}

function updateFeature(
  product: DesktopUpdateProduct,
  index: number,
  apply: (feature: DesktopFeaturePolicy) => void,
) {
  const feature = product.featureAllowlist?.features[index];
  if (feature) {
    apply(feature);
  }
}

function RangeInputs({
  channel,
  disabled,
  label,
  range,
  update,
}: {
  channel: 'stable' | 'rc';
  disabled: boolean;
  label: string;
  range?: DesktopFeatureVersionRange;
  update(field: keyof DesktopFeatureVersionRange, value: string): void;
}) {
  return (
    <div className="grid gap-2 md:grid-cols-2">
      <Input
        aria-label={`${label} 最低版本（包含）`}
        disabled={disabled}
        value={range?.minimumVersionInclusive || ''}
        placeholder={
          channel === 'stable' ? '1.6.0（包含）' : '1.7.0-rc.2（包含）'
        }
        onChange={(event) =>
          update('minimumVersionInclusive', event.target.value)
        }
      />
      <Input
        aria-label={`${label} 最高版本（不包含）`}
        disabled={disabled}
        value={range?.maximumVersionExclusive || ''}
        placeholder="无上限"
        onChange={(event) =>
          update('maximumVersionExclusive', event.target.value)
        }
      />
      <div className="text-xs text-muted-foreground md:col-span-2">
        生效区间：{formatDesktopFeatureRange(range)}
      </div>
    </div>
  );
}

export function DesktopFeatureAllowlistEditor({
  current,
  draft,
  product,
  readOnly,
  update,
}: DesktopFeatureAllowlistEditorProps) {
  const features = draft.featureAllowlist?.features || [];
  const featureRowSequence = useRef(0);
  const featureRowIDs = useRef<string[]>([]);
  const [activeFeatureRowID, setActiveFeatureRowID] = useState<string>();
  while (featureRowIDs.current.length < features.length) {
    featureRowIDs.current.push(
      `${product}-feature-row-${featureRowSequence.current}`,
    );
    featureRowSequence.current += 1;
  }
  if (featureRowIDs.current.length > features.length) {
    featureRowIDs.current.length = features.length;
  }
  const publishedKeys = new Set(
    (current?.featureAllowlist?.features || []).map((feature) => feature.key),
  );
  const featureRows = features.map((feature, featureIndex) => ({
    feature,
    featureIndex,
    publishedKey: publishedKeys.has(feature.key) ? feature.key : undefined,
    rowID: featureRowIDs.current[featureIndex],
  }));
  const activeFeatureRow = featureRows.find(
    ({ rowID }) => rowID === activeFeatureRowID,
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-medium">Feature 白名单</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              客户端只会收到当前版本命中的
              key；未被客户端消费的配置不会产生副作用。点击列表项右侧按钮查看或编辑详情。
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={readOnly || features.length >= 256}
            onClick={() => {
              const rowID = `${product}-feature-row-${featureRowSequence.current}`;
              featureRowIDs.current.push(rowID);
              featureRowSequence.current += 1;
              update((value) => {
                value.featureAllowlist?.features.push(
                  createEmptyDesktopFeature(),
                );
              });
              setActiveFeatureRowID(rowID);
            }}
          >
            新增 Feature
          </Button>
        </div>

        {features.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            当前产品没有 Feature 白名单配置
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            {featureRows.map(({ feature, featureIndex, rowID }) => {
              const overrideCount = DESKTOP_UPDATE_CHANNELS.reduce(
                (count, channel) =>
                  count +
                  Object.keys(feature.channels[channel].targetOverrides).length,
                0,
              );
              return (
                <article
                  key={rowID}
                  className="flex flex-wrap items-center justify-between gap-4 border-b border-border p-4 last:border-b-0"
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-medium text-muted-foreground">
                    {featureIndex + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-sm font-medium">
                      {feature.key || '未填写 Feature key'}
                    </div>
                    <div className="mt-1 truncate text-xs text-muted-foreground">
                      {feature.description || '未填写描述'}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {DESKTOP_UPDATE_CHANNELS.map((channel) => (
                        <Tag key={channel}>
                          {channel.toUpperCase()}：
                          {formatDesktopFeatureRange(
                            feature.channels[channel].defaultRange,
                          )}
                        </Tag>
                      ))}
                      {overrideCount ? (
                        <Tag color="blue">目标覆盖 {overrideCount} 项</Tag>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setActiveFeatureRowID(rowID)}
                  >
                    {readOnly ? '查看详情' : '编辑详情'}
                  </Button>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <Drawer
        open={Boolean(activeFeatureRow)}
        onClose={() => setActiveFeatureRowID(undefined)}
        title={
          activeFeatureRow
            ? `${activeFeatureRow.feature.key || '新增 Feature'} · ${product}`
            : `Feature 详情 · ${product}`
        }
        width={760}
      >
        {activeFeatureRow ? (
          <div className="space-y-5">
            <div className="grid gap-3 md:grid-cols-[1fr_1.5fr_auto]">
              <label
                className="space-y-1 text-sm"
                htmlFor={`${activeFeatureRow.rowID}-key`}
              >
                <span>Feature key</span>
                <Input
                  id={`${activeFeatureRow.rowID}-key`}
                  aria-label={`${product} Feature ${activeFeatureRow.featureIndex + 1} key`}
                  disabled={readOnly || Boolean(activeFeatureRow.publishedKey)}
                  value={activeFeatureRow.feature.key}
                  placeholder="workspace.exampleFeature"
                  onChange={(event) =>
                    update((value) =>
                      updateFeature(
                        value,
                        activeFeatureRow.featureIndex,
                        (item) => {
                          item.key = event.target.value;
                        },
                      ),
                    )
                  }
                />
                {activeFeatureRow.publishedKey ? (
                  <span className="block text-xs text-muted-foreground">
                    已发布 key 不可直接改名
                  </span>
                ) : null}
              </label>
              <label
                className="space-y-1 text-sm"
                htmlFor={`${activeFeatureRow.rowID}-description`}
              >
                <span>描述</span>
                <Input
                  id={`${activeFeatureRow.rowID}-description`}
                  aria-label={`${product} ${activeFeatureRow.feature.key} 描述`}
                  disabled={readOnly}
                  value={activeFeatureRow.feature.description}
                  placeholder="运营、审批与用途说明"
                  onChange={(event) =>
                    update((value) =>
                      updateFeature(
                        value,
                        activeFeatureRow.featureIndex,
                        (item) => {
                          item.description = event.target.value;
                        },
                      ),
                    )
                  }
                />
              </label>
              <Button
                className="self-end"
                size="sm"
                variant="destructive"
                disabled={readOnly}
                onClick={() => {
                  featureRowIDs.current.splice(
                    activeFeatureRow.featureIndex,
                    1,
                  );
                  update((value) => {
                    value.featureAllowlist?.features.splice(
                      activeFeatureRow.featureIndex,
                      1,
                    );
                  });
                  setActiveFeatureRowID(undefined);
                }}
              >
                删除
              </Button>
            </div>

            <div className="rounded-md bg-muted/30 p-3 text-xs text-muted-foreground">
              每个 Feature 至少需要在 stable 或 rc
              中配置一个生效区间后才能提交变更。
            </div>

            <div className="space-y-4">
              {DESKTOP_UPDATE_CHANNELS.map((channel) => {
                const policy = activeFeatureRow.feature.channels[channel];
                return (
                  <section
                    key={channel}
                    className="space-y-4 rounded-md border border-border p-4"
                  >
                    <div>
                      <h4 className="font-medium uppercase">{channel}</h4>
                      <div className="mt-1 text-xs text-muted-foreground">
                        生效区间
                      </div>
                    </div>

                    <RangeInputs
                      channel={channel}
                      disabled={readOnly}
                      label={`${product} ${activeFeatureRow.feature.key} ${channel} 生效区间`}
                      range={policy.defaultRange}
                      update={(field, value) =>
                        update((draftProduct) =>
                          updateFeature(
                            draftProduct,
                            activeFeatureRow.featureIndex,
                            (item) => {
                              const channelPolicy = item.channels[channel];
                              channelPolicy.defaultRange = updateOptionalRange(
                                channelPolicy.defaultRange,
                                field,
                                value,
                              );
                            },
                          ),
                        )
                      }
                    />

                    {DESKTOP_UPDATE_TARGETS.map((target) => {
                      const override = policy.targetOverrides[target.key];
                      const mode = override?.mode || 'inherit';
                      const releaseAvailable = isDesktopUpdateReleaseAvailable(
                        draft,
                        target.platform,
                        target.architecture,
                      );
                      return (
                        <div
                          key={target.key}
                          className="space-y-2 border-t border-border pt-3"
                        >
                          <div className="grid grid-cols-[1fr_1.2fr] items-center gap-3">
                            <div className="text-sm">
                              <div className="flex flex-wrap items-center gap-2">
                                {target.key}
                                {!releaseAvailable ? (
                                  <Tag>暂无发布产物</Tag>
                                ) : null}
                              </div>
                            </div>
                            <Select<'inherit' | 'disabled' | 'range'>
                              aria-label={`${product} ${activeFeatureRow.feature.key} ${channel} ${target.key} 覆盖模式`}
                              disabled={readOnly}
                              options={targetModeOptions}
                              value={mode}
                              onChange={(nextMode) =>
                                update((value) =>
                                  updateFeature(
                                    value,
                                    activeFeatureRow.featureIndex,
                                    (item) => {
                                      const overrides =
                                        item.channels[channel].targetOverrides;
                                      if (nextMode === 'inherit') {
                                        delete overrides[target.key];
                                      } else if (nextMode === 'disabled') {
                                        overrides[target.key] = {
                                          mode: 'disabled',
                                        };
                                      } else {
                                        overrides[target.key] = {
                                          mode: 'range',
                                          range: createRange(),
                                        };
                                      }
                                    },
                                  ),
                                )
                              }
                            />
                          </div>
                          {override?.mode === 'range' && override.range ? (
                            <RangeInputs
                              channel={channel}
                              disabled={readOnly}
                              label={`${product} ${activeFeatureRow.feature.key} ${channel} ${target.key}`}
                              range={override.range}
                              update={(field, fieldValue) =>
                                update((draftProduct) =>
                                  updateFeature(
                                    draftProduct,
                                    activeFeatureRow.featureIndex,
                                    (item) => {
                                      const range =
                                        item.channels[channel].targetOverrides[
                                          target.key
                                        ]?.range;
                                      if (range) {
                                        range[field] = fieldValue;
                                      }
                                    },
                                  ),
                                )
                              }
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </section>
                );
              })}
            </div>
          </div>
        ) : null}
      </Drawer>
    </>
  );
}
